import { useState, useEffect, useContext, useCallback } from "react"
import { Link } from "react-router-dom"
import { DateTime } from "luxon"
import AppBreadcrumb from "../../components/AppBreadcrumb/AppBreadcrumb"
import CustomTable from "../../components/CustomTable/CustomTable"
import LoaderFullPage from "../../components/Loaders"
import { LogError } from "../../helpers/logger"
import StandardPageHeader from "../../components/StandardPageHeader/StandardPageHeader"

// GraphQL
import { useLazyQuery } from "@apollo/client"
import { REFERRAL_LIST_PARTICIPANT } from "./query"

// Styles
import customStyles from "../../styles/customDataForms/CustomDataForms.module.css"
import customDataTableStyles from "../../styles/customTable/CustomTable.module.css"

// Context
import { UserContext } from "../../context/userContext"

//Routes
import ROUTES from "../../components/Routes/routesContants"

// Referral API calls
import { RefApiRefEntityStatusListPost } from "./ReferralsAPI"

export default function Referrals() {
  const userCtx = useContext(UserContext)
  const [isLoading, setIsLoading] = useState(true)
  const [refData, setRefData] = useState([{}])
  const [refStatus, setRefStatus] = useState(undefined)
  const [isLoadingRefStatus, setIsLoadingRefStatus] = useState(true)
  const viewReferralLink = ROUTES.PARTICIPANT_REFERRALS_DETAIL
  const query = REFERRAL_LIST_PARTICIPANT
  const queryVars = { pid: userCtx.participant_id || null }

  useEffect(() => {
    getReferrals()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [getReferrals] = useLazyQuery(query, {
    variables: queryVars,
    fetchPolicy: "network-only",
    onCompleted: (result) => {
      try {
        if (result !== undefined && result.referrals !== null) {
          const dataParsed = JSON.parse(result.referrals.data)

          if (dataParsed.length > 0) {
            const ids = dataParsed.map((x) => {
              return x.idStamp
            })
            const preppedData = dataParsed.map((x) => {
              const org_name = result.orgs.find(
                (org) => org.organization_id === x.organization_id,
              )?.organization_name

              if (!org_name) {
                console.log(`Unknown organization: ${x.organization_id}`)
              }
              const session_date_local = !!x.session_date
                ? DateTime.fromISO(x.session_date)
                    .toLocal()
                    .toFormat("MM/dd/yyyy")
                : ""

              return {
                ...x,
                refEntityStatus: "Loading...",
                organization_name: org_name || "",
                session_date_local,
                view: (
                  <Link
                    to={`${viewReferralLink.substring(
                      0,
                      viewReferralLink.lastIndexOf("/"),
                    )}/${x.idStamp}`}
                    state={{
                      idStamp: x.idStamp,
                      previousTitle: "Referrals",
                      previousRoute: "/participant/referrals",
                    }}
                    className={customStyles.actionButton}
                  >
                    View
                  </Link>
                ),
              }
            })
            setRefData(preppedData)
            setIsLoadingRefStatus(true)

            RefApiRefEntityStatusListPost(userCtx.tokens.getMainToken(), ids)
              .then((result) => {
                setRefStatus(result)
              })
              .catch((ex) => {
                console.log(ex)

                if (ex.isAxiosError) {
                  if (ex.response !== undefined) {
                    LogError(
                      "An error ocurred while retrieving the partner status: " +
                        ex.response.data,
                    )
                  } else {
                    LogError("Unable to load partner status: " + ex.message)
                    setRefStatus(null)
                  }
                } else {
                  LogError(ex)
                }
              })
          } else {
            setRefData(null)
          }
        } else {
          setRefData(null)
        }
        setIsLoading(false)
      } catch (ex) {
        LogError(ex)
        setIsLoading(false)
      }
    },
    onError: (error) => {
      LogError(error.message)
      setIsLoading(false)
    },
  })

  useEffect(() => {
    if (
      refData != null &&
      refData.length > 0 &&
      refStatus !== undefined &&
      isLoadingRefStatus
    ) {
      setIsLoadingRefStatus(false)
      let preppedData = []
      if (refStatus === null) {
        preppedData = refData.map((x) => {
          return { ...x, refEntityStatus: "Unknown" }
        })
      } else if (refStatus.length > 0) {
        preppedData = refData.map((x) => {
          return {
            ...x,
            refEntityStatus: refStatus.some((y) => y.referralId === x.idStamp)
              ? refStatus.filter((y) => y.referralId === x.idStamp)[0]
                  .referralEntityStatus
              : "Pending",
          }
        })
      } else {
        preppedData = refData.map((x) => {
          return { ...x, refEntityStatus: "Pending" }
        })
      }
      setRefData(preppedData)
    }
  }, [refData, refStatus, isLoadingRefStatus])

  const headers = () => {
    return [
      {
        value: "organization_name",
        label: "Organization",
        size: 2,
      },
      {
        value: "session_date_local",
        label: "Date",
        size: 1,
      },
      {
        value: "template_title",
        label: "Source",
        size: 2,
      },
      {
        value: "referral_type",
        label: "Type",
        size: 1,
      },
      {
        value: "referral_entity_name",
        label: "Referral Partner",
        size: 2,
      },
      {
        value: "entityStatus",
        label: "Partner Status",
        size: 1,
      },
      {
        value: "status",
        label: "Referral Status",
        size: 2,
      },
      {
        value: "action",
        label: "",
        action: true,
        size: 1,
      },
    ]
  }

  const EntityStatusTemplate = (data) => {
    let className = ""
    switch (data) {
      case "Active":
        className = customDataTableStyles.active
        break
      case "Complete":
        className = customDataTableStyles.complete
        break
      case "Pending":
        className = customDataTableStyles.pending
        break
      default:
    }

    return (
      <div className={`${customDataTableStyles.tag} ${className}`}>{data}</div>
    )
  }

  const StatusTemplate = (data) => {
    let className = ""
    switch (data) {
      case "Active referral":
        className = customDataTableStyles.active
        break
      case "Completed referral":
        className = customDataTableStyles.complete
        break
      case "Pending":
        className = customDataTableStyles.pending
        break
      case "Incomplete referral":
        className = customDataTableStyles.incomplete
        break
      default:
    }

    return (
      <div className={`${customDataTableStyles.tag} ${className}`}>{data}</div>
    )
  }

  const mapOrganization = (organization) => {
    if (!organization) {
      return "Unknown Organization"
    }
    if (organization === false) {
      return "Unknown Organization"
    }
    return organization
  }

  const mapData = useCallback(() => {
    return refData?.map((d) => {
      return {
        organization_name: mapOrganization(d.organization_name),
        session_date_local: d.session_date_local,
        template_title: d.template_title,
        referral_type: d.values ? d.values.referral_type : "",
        referral_entity_name: d.values ? d.values.referral_entity_name : "",
        entityStatus: EntityStatusTemplate(d.refEntityStatus),
        status: d.values ? StatusTemplate(d.values.referral_status) : "",
        action: d.view,
      }
    })
  }, [refData])

  useEffect(() => {
    mapData()
  }, [mapData])

  const filters = () => {
    return [
      {
        value: "search",
        label: "",
        placeholder: "Keyword Search",
        showClear: true,
        type: "search",
      },
    ]
  }

  const sorting = () => {
    return [
      {
        value: "sortBy",
        label: "Sort by",
        options: [
          { name: "Organization", code: "organization_name" },
          { name: "Date", code: "session_date_local" },
          { name: "Source", code: "template_title" },
          { name: "Type", code: "referral_type" },
          { name: "Referral", code: "referral_entity_name" },
          { name: "Referral Status", code: "status" },
          { name: "Partner Status", code: "entityStatus" },
        ],
        default: ["organization_name", "session_date_local"],
        placeholder: "Select option",
        showClear: true,
        type: "multiselect",
      },
    ]
  }

  return (
    <div className="main-content">
      <AppBreadcrumb items={[{ label: "Referrals" }]} />
      <div className="w-full pb-5">
        <StandardPageHeader
          title="Referrals"
          refreshData={() => {
            setIsLoading(true)
            getReferrals()
          }}
        />

        <hr className="line" />

        {isLoading ? (
          <LoaderFullPage />
        ) : (
          <CustomTable
            headers={headers()}
            collection={mapData()}
            filters={filters()}
            sorting={sorting()}
          />
        )}
      </div>
    </div>
  )
}
